import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { WordsProvider } from '../features/clients/words-ui';
import { useAuth } from '../features/auth/useAuth';
import { usePermissions } from '../features/auth/usePermissions';
import { OTKAZ_STROKOY } from '../features/auth/otkaz';
import { prochitano, type Vkladka } from '../features/obrashcheniya/api';
import { Ochered } from '../features/obrashcheniya/components/Ochered';
import { Razbor } from '../features/obrashcheniya/components/Razbor';
import {
  BAR_KEY,
  OCHERED_KEY,
  useOchered,
  useRazbor,
  useZaderzhka,
} from '../features/obrashcheniya/hooks';

import './layt.css';
import { PlashkaUvedomleniy } from './PlashkaUvedomleniy';
import { ShapkaLayta } from './ShapkaLayta';
import { KartochkaKlientki } from './KartochkaKlientki';
import { VhodLayt } from './VhodLayt';

/**
 * ЛАЙТ-ПЕРЕПИСКА НА ТЕЛЕФОН (`s608`, п.17 владельца).
 *
 * Тот же экран обращений, что в панели, без меню и разделов: список — раскрытие
 * — ответ. Компоненты берутся ИМПОРТОМ из экрана обращений, не копией: правка
 * экрана доезжает сюда сама.
 *
 * ⛔ ВСЕГДА УЗКАЯ РАСКЛАДКА, БЕЗ ЗАМЕРА ШИРИНЫ. Панель ветвится по ширине окна
 * (`useIsMobile`), потому что она одна на телефон и ноутбук. Лайт — телефон по
 * назначению: разложи его в две колонки на планшете — и получилась бы третья
 * раскладка, которую никто не принимал глазами.
 *
 * ⛔ ВЫСОТА В `dvh` (`s605`, телефон владельца): панель браузера то выезжает,
 * то прячется, и `vh` пересчитывает раскладку рывком на каждом движении пальца.
 */
const PAMYAT_FILIALA = 's603.filial';
const PAMYAT_VKLADKI = 's604.vkladka';

const VKLADKI: Vkladka[] = ['aktivnye', 'novye', 'v_rabote', 'bez_klientki', 'razobrannye'];

function vkladkaIzPamyati(): Vkladka | null {
  try {
    const iz = localStorage.getItem(PAMYAT_VKLADKI);
    return VKLADKI.includes(iz as Vkladka) ? (iz as Vkladka) : null;
  } catch {
    return null;
  }
}

export function LaytEkran() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Serednia>Загрузка…</Serednia>;
  }

  if (!isAuthenticated) {
    return <VhodLayt />;
  }

  return <Perepiska />;
}

function Perepiska() {
  const { can } = usePermissions();
  const qc = useQueryClient();
  const mesto = useLocation();
  const veduschiy = useNavigate();

  const [vkladka, setVkladka] = useState<Vkladka | null>(() => vkladkaIzPamyati());
  const [filialId, setFilialId] = useState<number | null>(() => {
    try {
      const sohranyonnoe = localStorage.getItem(PAMYAT_FILIALA);
      return sohranyonnoe === null || sohranyonnoe === '' ? null : Number(sohranyonnoe);
    } catch {
      return null;
    }
  });
  const [poisk, setPoisk] = useState('');
  const [otkryto, setOtkryto] = useState<number | null>(null);
  const [kartochkaRukoy, setKartochkaRukoy] = useState(false);

  const otlozhennyyPoisk = useZaderzhka(poisk);
  const mozhno = can('inquiry.queue.view');

  const ochered = useOchered(
    { vkladka, filial_id: filialId, poisk: otlozhennyyPoisk },
    mozhno,
  );
  const razbor = useRazbor(otkryto);

  // `s731`: у беседы открытого закрытого диалога уже есть новое обращение — переходим в него сами.
  const otkrytaZanovo = razbor.data?.id === otkryto ? razbor.data?.otkryta_zanovo ?? null : null;
  useEffect(() => {
    if (otkrytaZanovo === null) return undefined;
    const timer = window.setTimeout(() => setOtkryto(otkrytaZanovo), 0);
    return () => window.clearTimeout(timer);
  }, [otkrytaZanovo]);

  useEffect(() => {
    try {
      if (filialId === null) localStorage.removeItem(PAMYAT_FILIALA);
      else localStorage.setItem(PAMYAT_FILIALA, String(filialId));
    } catch {
      /* приватное окно — память необязательна, экран обязан работать */
    }
  }, [filialId]);

  /*
   * ⛔⛔ «ОТКРЫТЬ КАРТОЧКУ» ВНУТРИ РАЗБОРА ВЕДЁТ В ПАНЕЛЬ, А НЕ НАРУЖУ.
   * Это ЕДИНСТВЕННЫЙ вход в мини-карточку — своей кнопки с именем шапка лайта
   * не заводит: имя клиентки уже стоит в шапке разбора, и второе такое же на
   * экране было бы дублем величины (§31.8).
   *
   * В меню действий разбора живёт ссылка на карточку клиентки — она написана
   * для панели, где такой экран есть. В лайте его нет и быть не должно
   * (решение владельца Б, 18.09: «в админку за историей не гоняем»), но и
   * мёртвой кнопка остаться не может: человек нажал — обязано что-то
   * произойти. Поэтому адрес перехватывается здесь и открывает мини-карточку
   * той же панелью, что и нажатие на имя в шапке.
   *
   * Перехват стоит ЗДЕСЬ, а не правкой разбора: файл экрана обращений общий с
   * панелью, и трогать его ради лайта значило бы чинить свой экран чужими
   * руками.
   */
  const kartochkaIzAdresa = /^\/clients\/\d+/.test(mesto.pathname);
  const kartochkaOtkryta = kartochkaRukoy || kartochkaIzAdresa;

  const zakrytKartochku = useCallback(() => {
    setKartochkaRukoy(false);

    // Адрес возвращается к переписке, иначе панель открылась бы снова сама.
    if (kartochkaIzAdresa) {
      veduschiy('/', { replace: true });
    }
  }, [kartochkaIzAdresa, veduschiy]);

  if (!mozhno) {
    return (
      <div className="ops-skin text-ops-text">
        <ShapkaLayta />
        <p className="p-4 text-ops-body">{OTKAZ_STROKOY}</p>
      </div>
    );
  }

  const slovar = ochered.data ?? razbor.data;

  const otkryt = (id: number) => {
    setOtkryto(id);
    zakrytKartochku();

    // Прочитано = прочитано, и сразу везде (`s605`): оба запроса перечитываются
    // немедленно, иначе счётчик в шапке до пяти секунд показывал бы старое.
    void prochitano(id)
      .then(() => {
        void qc.invalidateQueries({ queryKey: BAR_KEY });
        void qc.invalidateQueries({ queryKey: OCHERED_KEY });
      })
      .catch(() => undefined);
  };

  return (
    <WordsProvider
      hints={slovar?.hints ?? {}}
      words={slovar?.words ?? { labels: {}, texts: {} }}
    >
      <div className="ops-skin layt-vid flex h-[100dvh] flex-col overflow-hidden bg-[#F5F6FA] text-ops-text">
        <ShapkaLayta />

        {/*
          ⛔ ПЛАШКА УВЕДОМЛЕНИЙ — ТОЛЬКО НА СПИСКЕ (владелец, 18.09, `s615`).
          В открытом диалоге она отняла бы строку у переписки и встала бы
          между шапкой собеседницы и лентой; вопрос «включить уведомления»
          там не решают. Пропадает сама, как только подписка есть.
        */}
        {otkryto === null && <PlashkaUvedomleniy />}

        {/*
          ⛔⛔ В УПОР ДО КРАЁВ И ДО НИЗА (владелец, 18.09.2026, с боевого
          телефона: «весь блок без свободных поле, в упор до низу, в упор до
          боков!!!!!!!!!!!»).

          Здесь стоял отступ в 16 точек — он был сведён с принятым экраном
          панели и на глаз казался правильным. На телефоне он таковым не
          оказался: пустая рамка по кругу отнимала у переписки полосу с
          каждой стороны, а лайт занимает телефон целиком, подложки под
          карточкой нет. Отступ снят, скруглённая рамка карточки гасится
          правилом `.layt-kraya` — своим, не правкой экрана панели.

          ⛔ Единственное, что осталось, — безопасная зона выреза: без неё
          переписка уезжает под скруглённый угол телефона.
        */}
        <div
          className="layt-kraya min-h-0 flex-1"
          style={{
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          {otkryto === null ? (
            /*
              ⛔ КЛАСС ВИДА — ЗАЦЕПКА ДЛЯ ОТДЕЛКИ ЛАЙТА (`s615`). Экран списка
              приезжает общим компонентом (он же в панели), и трогать его
              нельзя: правка отделки утащила бы панель за собой. Отделка живёт
              в `layt.css` и цепляется за ЭТОТ класс — правило видно одним
              файлом и панели не касается.
            */
            <div className="layt-spisok flex h-full flex-col">
              <Ochered
                dannye={ochered.data}
                gruzitsya={ochered.isLoading}
                vybran={otkryto}
                poisk={poisk}
                onPoisk={setPoisk}
                onVkladka={(v) => {
                  setOtkryto(null);
                  setVkladka(v);
                  try {
                    localStorage.setItem(PAMYAT_VKLADKI, v);
                  } catch {
                    /* приватное окно — память необязательна */
                  }
                }}
                onFilial={(id) => {
                  setFilialId(id);
                  setOtkryto(null);
                }}
                onOtkryt={otkryt}
              />
            </div>
          ) : (
            <div className="layt-dialog flex h-full flex-col">
              <Razbor
                dannye={razbor.data}
                gruzitsya={razbor.isLoading}
                onNazad={() => setOtkryto(null)}
                onOtkryt={otkryt}
              />
            </div>
          )}
        </div>

        {kartochkaOtkryta && razbor.data != null && (
          <KartochkaKlientki dannye={razbor.data} onZakryt={zakrytKartochku} />
        )}
      </div>
    </WordsProvider>
  );
}

function Serednia({ children }: { children: React.ReactNode }) {
  return (
    <div className="ops-skin grid h-[100dvh] place-items-center text-ops-body text-ops-text-secondary">
      {children}
    </div>
  );
}
